import { captchaAxios, masterAxios } from 'api/config'
import { Chat } from 'types/chat'
import { store } from 'store/index'
import { messagesActions, messagesSelectors, selectChatId } from 'store/messages'
import { ChatMessage } from 'types/chat-message'
import axios from 'axios'
import { settingsActions, settingsSelectors } from 'store/settings'
import { isEmpty, map } from 'lodash'
import { UploadService } from '@runelab/odin-common'
import { firebaseAnalytics } from 'services/firebase'
import { useQuery } from '@tanstack/react-query'
import { showErrorToast } from 'utils/toast'
import i18n from 'i18next'

export const CHAT_QUERY_KEY = 'chat'

type OpenNewChatParams = {
  areaId?: string
}

export const openNewChat = async ({ areaId }: OpenNewChatParams = {}) => {
  const { dispatch } = store
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)
  const isMultiArea = settingsSelectors.isMultiAreaFlow(state)
  const lang = settingsSelectors.lang(state)

  const httpClient = isLogged ? axios : captchaAxios
  const requestAreaId = isMultiArea ? undefined : areaId

  try {
    const { data: chat } = await httpClient.post<Chat>('chats/bot', { culture: lang, areaId: requestAreaId })
    console.debug({ chat })
    dispatch(messagesActions.addChat(chat))
    dispatch(messagesActions.setChatId(chat._id))
    firebaseAnalytics.trackOpenChatArea(requestAreaId ?? chat.areaIds?.[0], {
      user: chat.user,
      type: isLogged ? 'logged' : 'guest',
    })
  } catch (e) {
    console.error({ e })
    if (isMultiArea) showErrorToast(i18n.t('multiAreaChatError'))
    return Promise.reject(e)
  }
}

export const getChatMessages = async (chatId: string) => {
  try {
    const { data } = await masterAxios.get<ChatMessage[]>('chat-messages', {
      params: {
        chatId,
        sort: 'createdAt',
        limit: 100,
      },
    })
    console.debug({ data })
    store.dispatch(messagesActions.setChatMessages({ chatId, messages: data }))
    return data
  } catch (e) {
    console.error({ e })
    return []
  }
}

export const getMyChats = async ({ areaId }: { areaId?: string }) => {
  try {
    const { data } = await axios.get<Chat[]>('chats/me', { params: { areaIds: areaId, limit: 100 } })
    store.dispatch(messagesActions.addChats(data))

    const chatId = selectChatId(store.getState())

    const chatIndex = data.findIndex((chat) => chat._id === chatId)

    if (chatIndex === -1) {
      // The open chat is gone: clear the selection (per-chat status needs no separate reset).
      store.dispatch(messagesActions.setChatId(undefined))
      store.dispatch(settingsActions.setTicketReminderVisible(false))
    }
  } catch (e) {
    console.error({ e })
  }
}

type MessageToOperatorParams = {
  message: string
  files: File[]
}

const createUploads = async (files: File[]) => {
  const service = new UploadService(axios)
  return await Promise.all(
    files.map((file) => {
      return service.uploadFile({
        file,
        uploadFileParam: {
          additionalInfo: {},
          linkedEntityId: '',
          linkedEntityType: '',
        },
      })
    }),
  )
}

export const sentMessageToOperator = async ({ message, files }: MessageToOperatorParams) => {
  const state = store.getState()
  const chatId = selectChatId(state)
  try {
    const uploads = await createUploads(files)
    console.debug({ uploads })
    const { data } = await axios.post('chat-messages', {
      message,
      chatId,
      operatorAttachmentIds: map(uploads, '_id'),
    })
    console.debug({ newMessage: data })
    store.dispatch(messagesActions.addIncomingMessage(data))
  } catch (e) {
    console.error({ e })
  }
}

export const assignGuestChatsToCurrentUser = async () => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)
  const guestChats = messagesSelectors.guestChats(state)

  const chatIds = map(guestChats, '_id')
  console.debug({ guestChats })

  if (isLogged) {
    if (isEmpty(chatIds)) {
      return Promise.resolve()
    }
    await axios.put('chats/assign/me', {
      chatIds: map(guestChats, '_id'),
    })
    await getMyChats({})
  } else {
    return Promise.resolve()
  }
}

export const markChatAsRead = async (chatId: string) => {
  const state = store.getState()
  const isLogged = settingsSelectors.isLogged(state)

  // Skip if user is not logged in (guest users are not in participants)
  if (!isLogged) return

  try {
    const { data } = await captchaAxios.patch<Chat>(`chats/${chatId}/read`)
    store.dispatch(messagesActions.editChat(data))
  } catch (e) {
    console.error({ e })
  }
}

export const exportChat = async (chatId: string, email: string) => {
  try {
    const { data } = await axios.get(`chats/${chatId}/export/${email}`)
    console.debug({ exportChat: data })
  } catch (e) {
    console.error({ e })
  }
}

export const getChat = async (chatId: string): Promise<Chat> => {
  const { data } = await axios.get<Chat>(`chats/${chatId}`)
  console.debug('data', data)
  store.dispatch(messagesActions.setChat(data))
  return data
}

export const useGetChat = (chatId: string | undefined) => {
  return useQuery({
    queryKey: [CHAT_QUERY_KEY, chatId],
    queryFn: () => getChat(chatId!),
    enabled: !!chatId,
    gcTime: 0,
    staleTime: 0, // Consider data fresh for 30 seconds
  })
}
